// src/App.jsx
import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, Navigate, useLocation } from 'react-router-dom';
import HomePage from './Home';
import AvailableCars from './AvailableCars';
import FleetManagement from './FleetManagement';
import Contact from './Contact';
import ThingsToDo from './ThingsToDo';
import Admin from './Admin';
import './index.css';
import logo from './assets/logo.png';

document.documentElement.classList.add('dark');

const TURO_PROFILE = 'https://turo.com/us/en/drivers/33571187';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/available-cars', label: 'Available Cars' },
  { to: '/fleet-management', label: 'Fleet Management' },
  { to: '/ThingsToDo', label: 'Experience Vegas' },
  { to: '/contact', label: 'Contact' },
];

// Remember ad-click parameters so the owner form can send them with the lead,
// even if the visitor lands on one page and applies from another.
const TRACKING_KEYS = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

function captureTracking() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    TRACKING_KEYS.forEach((k) => {
      if (params.get(k)) found[k] = params.get(k);
    });
    if (Object.keys(found).length) {
      sessionStorage.setItem('pfr_tracking', JSON.stringify(found));
    }
  } catch (e) {
    /* storage can be blocked; tracking is optional */
  }
}

// New page: start at the top, or at the #section the link points to
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const target = hash && document.getElementById(hash.slice(1));
    if (target) target.scrollIntoView({ block: 'start' });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    captureTracking();
  }, []);

  const linkClass = ({ isActive }) =>
    `inline-flex min-h-[2.75rem] items-center border-b-2 px-3 text-[0.95rem] font-medium transition-colors ${
      isActive
        ? 'border-[var(--bronze)] text-white'
        : 'border-transparent text-[var(--muted)] hover:text-white'
    }`;

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen overflow-x-hidden">
        <header
          className="sticky top-0 z-50"
          style={{
            background: 'rgba(14,13,11,0.86)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div className="container-x flex h-16 items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3" onClick={() => setMenuOpen(false)}>
              <img
                src={logo}
                alt=""
                className="h-9 w-9 rounded-full object-cover"
                style={{ border: '1px solid var(--bronze)' }}
              />
              <span className="display text-lg">Prime Fleet Rentals</span>
            </Link>

            <nav className="hidden items-center md:flex" aria-label="Main">
              {navLinks.map(({ to, label }) => (
                <NavLink key={to} to={to} end={to === '/'} className={linkClass}>
                  {label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <Link to="/fleet-management" className="btn btn-primary hidden !min-h-[2.5rem] !px-5 !py-2 text-[0.95rem] sm:inline-flex">
                List your car
              </Link>
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-full md:hidden"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                onClick={() => setMenuOpen((o) => !o)}
              >
                <span className="relative block h-3.5 w-6">
                  <span
                    className="absolute left-0 block h-0.5 w-6 rounded bg-white transition-all duration-200"
                    style={{ top: menuOpen ? '6px' : '0', transform: menuOpen ? 'rotate(45deg)' : 'none' }}
                  />
                  <span
                    className="absolute left-0 block h-0.5 w-6 rounded bg-white transition-all duration-200"
                    style={{ top: '6px', opacity: menuOpen ? 0 : 1 }}
                  />
                  <span
                    className="absolute left-0 block h-0.5 w-6 rounded bg-white transition-all duration-200"
                    style={{ top: menuOpen ? '6px' : '12px', transform: menuOpen ? 'rotate(-45deg)' : 'none' }}
                  />
                </span>
              </button>
            </div>
          </div>

          {menuOpen && (
            <div id="mobile-menu" className="md:hidden" style={{ borderTop: '1px solid var(--line)' }}>
              <nav className="container-x flex flex-col py-2" aria-label="Mobile">
                {navLinks.map(({ to, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex min-h-[3rem] items-center text-lg font-medium ${
                        isActive ? 'text-white' : 'text-[var(--muted)]'
                      }`
                    }
                    style={{ borderBottom: '1px solid var(--line)' }}
                  >
                    {label}
                  </NavLink>
                ))}
                <Link
                  to="/fleet-management"
                  onClick={() => setMenuOpen(false)}
                  className="btn btn-primary my-4"
                >
                  List your car
                </Link>
              </nav>
            </div>
          )}
        </header>

        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/available-cars" element={<AvailableCars />} />
            <Route path="/fleet-management" element={<FleetManagement />} />
            <Route path="/ThingsToDo" element={<ThingsToDo />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>

        <footer style={{ borderTop: '1px solid var(--line)', background: '#0a0908' }}>
          <div className="container-x grid gap-8 py-10 sm:grid-cols-[1fr_auto]">
            <div>
              <p className="display text-lg">Prime Fleet Rentals</p>
              <p className="muted mt-1 text-sm">Las Vegas, Nevada</p>
              <p className="muted mt-4 text-sm">
                <a className="link-quiet" href={TURO_PROFILE} target="_blank" rel="noopener noreferrer">
                  Our Turo host profile
                </a>
              </p>
            </div>
            <nav className="flex flex-wrap gap-x-6 gap-y-1 text-sm sm:max-w-xs sm:justify-end" aria-label="Footer">
              {navLinks.map(({ to, label }) => (
                <Link key={to} to={to} className="muted inline-flex min-h-[2.25rem] items-center hover:text-white">
                  {label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="container-x pb-8">
            <p className="muted text-xs">
              &copy; {new Date().getFullYear()} Prime Fleet Holdings LLC. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
}
